import { supabase } from '../lib/supabase';

export async function openaiChat(prompt: string): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-proxy', {
      body: {
        type: 'chat',
        data: {
          prompt,
          language: 'EN'
        }
      }
    });

    if (error) {
      console.error("Supabase function error:", error);
      return "";
    }

    return data?.response || "";
  } catch (e) {
    console.error("OpenAI error", e);
    return "";
  }
}

export async function generateCallScript(
  name: string,
  need: string,
  language: string = 'EN'
): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-proxy', {
      body: {
        type: 'call_script',
        data: {
          name,
          needDescription: need,
          language
        }
      }
    });

    if (error) {
      console.error("Supabase function error:", error);
      return "I apologize, but I'm unable to generate a call script at the moment. Please try again later.";
    }

    return data?.response || "I apologize, but I'm unable to generate a call script at the moment. Please try again later.";
  } catch (error) {
    console.error("Error generating call script:", error);
    return "I apologize, but I'm unable to generate a call script at the moment. Please try again later.";
  }
}

export async function generateAppealLetter(
  program: string,
  detail: string,
  language: string = 'EN'
): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-proxy', {
      body: {
        type: 'appeal_letter',
        data: {
          program,
          detail,
          language
        }
      }
    });

    if (error) {
      console.error("Supabase function error:", error);
      return "I apologize, but I'm unable to generate an appeal letter at the moment. Please try again later.";
    }

    return data?.response || "I apologize, but I'm unable to generate an appeal letter at the moment. Please try again later.";
  } catch (error) {
    console.error("Error generating appeal letter:", error);
    return "I apologize, but I'm unable to generate an appeal letter at the moment. Please try again later.";
  }
}

export interface AIProgram {
  id: string;
  name: string;
  description: string;
  eligibility: string[];
  services: string[];
  contact: {
    phone: string;
    email: string;
    website: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  hours: {
    [key: string]: string;
  };
  languages: string[];
  distance: number;
}

export async function generateProgramRecommendations(
  zipCode: string,
  need: 'rent' | 'food' | 'shelter' | 'legal' | 'jobs',
  radius: number = 25,
  language: string = 'EN'
): Promise<AIProgram[]> {
  try {
    // Call OpenAI API directly since Edge Functions aren't available locally
    const openaiApiKey = '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';
    
    const needDescriptions = {
      rent: 'rental assistance, housing support, eviction prevention',
      food: 'food assistance, food banks, meal programs, nutrition support',
      shelter: 'emergency shelter, transitional housing, homeless services',
      legal: 'legal aid, legal clinics, tenant rights, housing law',
      jobs: 'job training, employment services, career development, workforce programs'
    };

    const languageInstructions = {
      EN: "Generate all content in English.",
      ES: "Generate all content in Spanish.",
      ZH: "Generate all content in Chinese (Simplified).",
      VI: "Generate all content in Vietnamese.",
      FIL: "Generate all content in Filipino.",
      KO: "Generate all content in Korean.",
      AR: "Generate all content in Arabic."
    };

    const prompt = `Generate 3-5 realistic social service programs near zip code ${zipCode} that provide ${needDescriptions[need]}. 

${languageInstructions[language as keyof typeof languageInstructions] || languageInstructions.EN}

Return the response as a valid JSON array with this exact structure for each program:
{
  "id": "unique-id",
  "name": "Program Name",
  "description": "Comprehensive description of services offered, eligibility requirements, and how the program helps people in need. Include specific details about what assistance is provided.",
  "eligibility": ["requirement 1", "requirement 2", "requirement 3"],
  "services": ["service 1", "service 2", "service 3", "service 4"],
  "contact": {
    "phone": "phone number",
    "email": "email@example.org",
    "website": "https://example.org"
  },
  "address": {
    "street": "street address",
    "city": "city name",
    "state": "state abbreviation",
    "zip": "zip code"
  },
  "hours": {
    "Monday": "9:00 AM - 5:00 PM",
    "Tuesday": "9:00 AM - 5:00 PM",
    "Wednesday": "9:00 AM - 5:00 PM",
    "Thursday": "9:00 AM - 5:00 PM",
    "Friday": "9:00 AM - 5:00 PM"
  },
  "languages": ["en", "es"],
  "distance": 2.5
}

Make the programs realistic with actual organization names, addresses within ${radius} miles of the zip code, and appropriate services for ${need} assistance. Include a mix of government agencies, non-profits, and community organizations.`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${openaiApiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful assistant that generates realistic social service program information in valid JSON format. Always return properly formatted JSON arrays. Focus on providing detailed, accurate information that would be helpful for people seeking assistance.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const responseData = await response.json();
    const content = responseData.choices[0].message.content.trim();
    
    // Try to extract JSON from the response
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const programs = JSON.parse(jsonMatch[0]);
      return programs.map((program: any, index: number) => ({
        ...program,
        id: program.id || `ai-generated-${index}`,
        distance: program.distance || Math.random() * radius
      }));
    }
    
    // Fallback: return realistic mock data if JSON parsing fails
    console.warn("Failed to parse AI response as JSON, using fallback data:", content);
    return getFallbackPrograms(zipCode, need, radius, language);
  } catch (error) {
    console.error("Error generating program recommendations:", error);
    return getFallbackPrograms(zipCode, need, radius, language);
  }
}

// Fallback function to provide realistic program data when AI fails
function getFallbackPrograms(zipCode: string, need: 'rent' | 'food' | 'shelter' | 'legal' | 'jobs', radius: number, language: string): AIProgram[] {
  const languageContent = {
    EN: {
      rent: {
        name: "Community Housing Assistance Program",
        description: "Provides emergency rental assistance and eviction prevention services for low-income families facing housing crises.",
        eligibility: ["Income below 200% of federal poverty level", "Facing eviction or housing crisis", "Resident of service area"],
        services: ["Rental assistance", "Eviction prevention", "Housing counseling", "Utility assistance"]
      },
      food: {
        name: "Community Food Bank Network",
        description: "Provides emergency food assistance, nutrition education, and SNAP application support for families in need.",
        eligibility: ["Income below 185% of federal poverty level", "Resident of service area", "Valid identification required"],
        services: ["Emergency food assistance", "SNAP application help", "Nutrition education", "Meal programs"]
      },
      shelter: {
        name: "Emergency Shelter & Transitional Housing",
        description: "Provides immediate emergency shelter and transitional housing with comprehensive support services for homeless individuals and families.",
        eligibility: ["Currently homeless", "Income below 200% of federal poverty level", "No active warrants", "Willing to participate in case management"],
        services: ["Emergency shelter", "Transitional housing", "Case management", "Employment assistance", "Mental health services"]
      },
      legal: {
        name: "Legal Aid Society",
        description: "Free legal services for low-income individuals facing housing, employment, and civil rights issues.",
        eligibility: ["Income below 125% of federal poverty level", "Legal issue within practice areas", "Resident of service area"],
        services: ["Housing law assistance", "Employment law", "Civil rights", "Family law", "Public benefits"]
      },
      jobs: {
        name: "Workforce Development Center",
        description: "Comprehensive job training and employment services including career counseling, skills training, and job placement assistance.",
        eligibility: ["Age 18+", "Unemployed or underemployed", "Resident of service area", "Willing to participate in training"],
        services: ["Job training programs", "Career counseling", "Resume writing", "Job placement", "Skills assessment"]
      }
    },
    ES: {
      rent: {
        name: "Programa de Asistencia de Vivienda Comunitaria",
        description: "Proporciona asistencia de alquiler de emergencia y servicios de prevención de desalojo para familias de bajos ingresos que enfrentan crisis de vivienda.",
        eligibility: ["Ingresos por debajo del 200% del nivel federal de pobreza", "Enfrentando desalojo o crisis de vivienda", "Residente del área de servicio"],
        services: ["Asistencia de alquiler", "Prevención de desalojo", "Asesoramiento de vivienda", "Asistencia de servicios públicos"]
      },
      food: {
        name: "Red de Bancos de Alimentos Comunitarios",
        description: "Proporciona asistencia alimentaria de emergencia, educación nutricional y apoyo para solicitudes de SNAP para familias necesitadas.",
        eligibility: ["Ingresos por debajo del 185% del nivel federal de pobreza", "Residente del área de servicio", "Identificación válida requerida"],
        services: ["Asistencia alimentaria de emergencia", "Ayuda para solicitudes de SNAP", "Educación nutricional", "Programas de comidas"]
      },
      shelter: {
        name: "Refugio de Emergencia y Vivienda Transitoria",
        description: "Proporciona refugio de emergencia inmediato y vivienda transitoria con servicios de apoyo integral para personas y familias sin hogar.",
        eligibility: ["Actualmente sin hogar", "Ingresos por debajo del 200% del nivel federal de pobreza", "Sin órdenes de arresto activas", "Dispuesto a participar en gestión de casos"],
        services: ["Refugio de emergencia", "Vivienda transitoria", "Gestión de casos", "Asistencia laboral", "Servicios de salud mental"]
      },
      legal: {
        name: "Sociedad de Asistencia Legal",
        description: "Servicios legales gratuitos para personas de bajos ingresos que enfrentan problemas de vivienda, empleo y derechos civiles.",
        eligibility: ["Ingresos por debajo del 125% del nivel federal de pobreza", "Problema legal dentro de las áreas de práctica", "Residente del área de servicio"],
        services: ["Asistencia en derecho de vivienda", "Derecho laboral", "Derechos civiles", "Derecho familiar", "Beneficios públicos"]
      },
      jobs: {
        name: "Centro de Desarrollo de la Fuerza Laboral",
        description: "Servicios integrales de capacitación laboral y empleo que incluyen asesoramiento profesional, capacitación en habilidades y asistencia para la colocación laboral.",
        eligibility: ["Edad 18+", "Desempleado o subempleado", "Residente del área de servicio", "Dispuesto a participar en capacitación"],
        services: ["Programas de capacitación laboral", "Asesoramiento profesional", "Redacción de currículum", "Colocación laboral", "Evaluación de habilidades"]
      }
    }
  };

  const content = languageContent[language as keyof typeof languageContent] || languageContent.EN;
  const programData = content[need];

  return [
    {
      id: `fallback-${need}-1`,
      name: programData.name,
      description: programData.description,
      eligibility: programData.eligibility,
      services: programData.services,
      contact: {
        phone: "(555) 123-4567",
        email: "info@communityprogram.org",
        website: "https://communityprogram.org"
      },
      address: {
        street: "123 Main Street",
        city: "Community City",
        state: "CA",
        zip: zipCode
      },
      hours: {
        "Monday": "9:00 AM - 5:00 PM",
        "Tuesday": "9:00 AM - 5:00 PM",
        "Wednesday": "9:00 AM - 5:00 PM",
        "Thursday": "9:00 AM - 5:00 PM",
        "Friday": "9:00 AM - 5:00 PM"
      },
      languages: ["en", "es"],
      distance: Math.random() * radius
    }
  ];
}
